import React, { CSSProperties, useEffect, useState } from 'react';
import { ProgramFinancialIncomes } from './tabs/program-financial-incomes';
import { ProgramFinancialExpenses } from './tabs/program-financial-expenses';
import {
  Grid,
  MessageSquare,
  Image,
  Settings,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import { Button } from 'primereact/button';
import { getProgramFinancialOperations } from './operations/get-program-financial-operations';
import { ProgramType } from '../../../../api/types/programs/program.type';
import globalProgramStore from '../../../../stores/program-store';
import { ProgramComparisonAmongBranchesDb } from '../../../../api/types/reports/finanace/program-comparison-among-branches';
import { ProgramBreakdownFinancialIncomeReport } from '../../../../api/types/reports/finanace/program-breakdown/incomes/program-financial-breakdown-incomes.type';
import { UserSummaryType } from '../../../../api/types/user/user-summary.type';
import { UserType } from '../../../../api/types/user/user-type';
import { UserPageDialog } from '../../../../component/ui/search-users/user-page-dialog/user-page-dialog';
import { YearlyProgramExpenseReport } from '../../../../api/types/reports/finanace/program-breakdown/incomes/program-financial-breakdown-expanses.type';
import { ProgramFinancialBalanceSheet } from './tabs/program-financial-balance-sheet';

const CategoryCard = ({
  category = 'Apps',
  percentage = '25%',
  iconColor = 'bg-green-400',
  icon = <Grid size={20} className="text-white" />,
}) => {
  return (
    <div className="bg-white rounded-xl shadow-sm p-6 flex justify-between items-center">
      <div>
        <h3 className="text-gray-600 font-medium mb-1">{category}</h3>
        <p className="text-2xl font-bold text-gray-800">{percentage}</p>
      </div>
      <div
        className={`${iconColor} rounded-full p-3 flex items-center justify-center`}
      >
        {icon}
      </div>
    </div>
  );
};

const CategoryCardGrid = () => {
  return (
    <div className="p-4 bg-gray-50 max-w-6xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <CategoryCard
          category="Apps"
          percentage="25%"
          iconColor="bg-green-400"
          icon={<Grid size={20} className="text-white" />}
        />
        <CategoryCard
          category="Messages"
          percentage="15%"
          iconColor="bg-yellow-400"
          icon={<MessageSquare size={20} className="text-white" />}
        />
        <CategoryCard
          category="Media"
          percentage="20%"
          iconColor="bg-blue-400"
          icon={<Image size={20} className="text-white" />}
        />
        <CategoryCard
          category="System"
          percentage="10%"
          iconColor="bg-purple-400"
          icon={<Settings size={20} className="text-white" />}
        />
      </div>
    </div>
  );
};

interface AsdData {
  title: string;
  str: string;
  icon?: React.ReactNode;
}
export const Asd: React.FC<AsdData> = ({ str, title, icon }) => {
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        padding: '24px 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minWidth: 0,
        minHeight: '120px',
        flex: 1,
        gap: '24px',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        <span
          style={{
            fontSize: '1.1rem',
            color: '#64748b',
            fontWeight: 500,
            marginBottom: 8,
          }}
        >
          {title}
        </span>
        <span
          style={{
            fontSize: '2rem',
            fontWeight: 700,
            color: '#0f172a',
            letterSpacing: '-1px',
          }}
        >
          {str}
        </span>
      </div>
      <div
        style={{
          background: 'linear-gradient(135deg, #f3f4f6 0%, #e0e7ef 100%)',
          borderRadius: '50%',
          width: 56,
          height: 56,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
        }}
      >
        {icon}
      </div>
    </div>
  );
};

interface TabButtonProps {
  label: string;
  icon: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
}

const TabButton: React.FC<TabButtonProps> = ({
  label,
  icon,
  isActive,
  onClick,
}) => {
  return (
    <button
      onClick={onClick}
      style={{
        background: isActive
          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          : '#fff',
        color: isActive ? '#fff' : '#64748b',
        border: isActive ? 'none' : '2px solid #e2e8f0',
        borderRadius: '12px',
        padding: '16px 32px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        fontSize: '16px',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        boxShadow: isActive
          ? '0 8px 25px rgba(102, 126, 234, 0.3)'
          : '0 2px 8px rgba(0,0,0,0.06)',
        transform: isActive ? 'translateY(-2px)' : 'translateY(0)',
        minWidth: '160px',
        justifyContent: 'center',
        outline: 'none',
      }}
      onMouseEnter={(e) => {
        if (!isActive) {
          e.currentTarget.style.borderColor = '#cbd5e1';
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
        }
      }}
      onMouseLeave={(e) => {
        if (!isActive) {
          e.currentTarget.style.borderColor = '#e2e8f0';
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
        }
      }}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
};

export const ProgramFinancialStatusOverview = () => {
  const [incomeData, setIncomeData] =
    useState<ProgramBreakdownFinancialIncomeReport>();
  const [expansesData, setExpansesData] =
    useState<YearlyProgramExpenseReport>();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'income' | 'expenses' | 'balance'>(
    'income',
  );

  useEffect(() => {
    const fetchProgramFinancial = async () => {
      const currentId = globalProgramStore.getCurrentId();
      if (typeof currentId !== 'number') return;
      const program: ProgramType | undefined =
        globalProgramStore.programs.get(currentId);
      if (!program) return;
      await getProgramFinancialOperations(
        program.metadata.branchId,
        program.metadata.abstractProgramId,
        program.metadata.programStartYear,
        program.metadata.id,
        setIncomeData,
        setExpansesData,
        setLoading,
      );
    };
    fetchProgramFinancial();
  }, []);

  const [selectedUserForDialog, setSelectedUserForDialog] = useState<
    UserSummaryType | undefined
  >();
  const handleUserClick = (user: UserSummaryType) => {
    setSelectedUserForDialog(user);
  };

  const closeUserDialog = () => {
    setSelectedUserForDialog(undefined);
  };

  return (
    <div
      style={{
        maxWidth: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Custom Tab Bar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '16px',
          marginBottom: '40px',
          padding: '24px',
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        }}
      >
        <TabButton
          label="הכנסות"
          icon={<TrendingUp size={20} />}
          isActive={activeTab === 'income'}
          onClick={() => setActiveTab('income')}
        />
        <TabButton
          label="הוצאות"
          icon={<TrendingDown size={20} />}
          isActive={activeTab === 'expenses'}
          onClick={() => setActiveTab('expenses')}
        />
        <TabButton
          label="מאזן"
          icon={<Grid size={20} />}
          isActive={activeTab === 'balance'}
          onClick={() => setActiveTab('balance')}
        />
      </div>

      {/* Tab Content */}
      <div style={{ minHeight: '500px' }}>
        {activeTab === 'income' && incomeData && (
          <div style={{ marginBottom: '48px' }}>
            <ProgramFinancialIncomes
              data={incomeData}
              onUserClick={handleUserClick}
            />
          </div>
        )}

        {activeTab === 'expenses' && expansesData && (
          <div style={{ marginBottom: '32px' }}>
            <ProgramFinancialExpenses data={expansesData} />
          </div>
        )}

        {activeTab === 'balance' && incomeData && expansesData && (
          <div style={{ marginBottom: '32px' }}>
            <ProgramFinancialBalanceSheet
              incomeData={incomeData}
              expensesData={expansesData}
            />
          </div>
        )}

        {/* No Data Messages */}
        {activeTab === 'income' && !incomeData && (
          <div
            style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: '#64748b',
              background: '#f8fafc',
              borderRadius: '16px',
              border: '2px dashed #cbd5e1',
            }}
          >
            <TrendingUp
              size={48}
              style={{ color: '#cbd5e1', marginBottom: '16px' }}
            />
            <h3
              style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600 }}
            >
              אין נתוני הכנסות זמינים
            </h3>
            <p style={{ margin: 0, fontSize: '14px' }}>
              הנתונים עדיין נטענים או שאין מידע זמין עבור התקופה הנבחרת
            </p>
          </div>
        )}

        {activeTab === 'expenses' && !expansesData && (
          <div
            style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: '#64748b',
              background: '#f8fafc',
              borderRadius: '16px',
              border: '2px dashed #cbd5e1',
            }}
          >
            <TrendingDown
              size={48}
              style={{ color: '#cbd5e1', marginBottom: '16px' }}
            />
            <h3
              style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600 }}
            >
              אין נתוני הוצאות זמינים
            </h3>
            <p style={{ margin: 0, fontSize: '14px' }}>
              הנתונים עדיין נטענים או שאין מידע זמין עבור התקופה הנבחרת
            </p>
          </div>
        )}

        {activeTab === 'balance' && (!incomeData || !expansesData) && (
          <div
            style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: '#64748b',
              background: '#f8fafc',
              borderRadius: '16px',
              border: '2px dashed #cbd5e1',
            }}
          >
            <Grid
              size={48}
              style={{ color: '#cbd5e1', marginBottom: '16px' }}
            />
            <h3
              style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600 }}
            >
              אין נתונים זמינים למאזן
            </h3>
            <p style={{ margin: 0, fontSize: '14px' }}>
              נדרשים נתוני הכנסות והוצאות כדי להציג את המאזן הכספי
            </p>
          </div>
        )}
      </div>

      <UserPageDialog
        userRole={UserType.STUDENT}
        selectedUser={selectedUserForDialog}
        setSelectedUser={setSelectedUserForDialog}
        closeDialog={closeUserDialog}
        onHide={closeUserDialog}
      />
    </div>
  );
};
